import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { DEFAULT_META_DESCRIPTION, SITE_ORIGIN } from '../seo/site';

const ABOUT_PAGE_URL = `${SITE_ORIGIN}/about`;

/** WebPage + FAQPage JSON-LD for `/about` (FAQ copy matches visible markup on that page). */
const AboutJsonLd: React.FC = () => {
  const { t, i18n } = useSafeTranslation();

  const jsonLd = useMemo(() => {
    const faqPairs = [
      {
        q: t('seo.home.faqQ1', 'What is Quizter Club?'),
        a: t(
          'seo.home.faqA1',
          'Quizter Club is a free web platform for timed trivia quizzes, PvP arena matches, and a structured career mode with missions, XP, and achievements.'
        ),
      },
      {
        q: t('seo.home.faqQ2', 'Do I need an account?'),
        a: t(
          'seo.home.faqA2',
          'You can browse as a guest. Creating an account lets you save XP, unlock achievements, join PvP rooms, and sync progress across sessions.'
        ),
      },
      {
        q: t('seo.home.faqQ3', 'How fast are quizzes?'),
        a: t(
          'seo.home.faqA3',
          'Standard solo rounds use a 30-second timer per question to keep pacing fair and competitive while you climb levels.'
        ),
      },
      {
        q: t('seo.home.faqQ4', 'What is PvP mode?'),
        a: t(
          'seo.home.faqA4',
          'PvP pits you against another player in real time on the same question set; faster correct answers can earn bonus XP.'
        ),
      },
      {
        q: t('seo.home.faqQ5', 'How do achievements work?'),
        a: t(
          'seo.home.faqA5',
          'You earn achievement points and badges by completing quizzes, missions, and special challenges. Your total score appears on dedicated leaderboards.'
        ),
      },
      {
        q: t('seo.home.faqQ6', 'Can I play on mobile?'),
        a: t(
          'seo.home.faqA6',
          'Yes. Quizter Club is a responsive web app and supports install to home screen as a PWA on supported browsers.'
        ),
      },
    ];

    const pageName = t('seo.home.schemaWebPageName', 'Quizter Club — interactive quizzes & competitive ranking');
    const pageDesc = t('seo.home.schemaWebPageDesc', DEFAULT_META_DESCRIPTION);

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${ABOUT_PAGE_URL}#webpage`,
          url: ABOUT_PAGE_URL,
          name: pageName,
          description: pageDesc,
          isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
        },
        {
          '@type': 'FAQPage',
          '@id': `${ABOUT_PAGE_URL}#faq`,
          url: ABOUT_PAGE_URL,
          mainEntity: faqPairs.map((pair) => ({
            '@type': 'Question',
            name: pair.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: pair.a,
            },
          })),
        },
      ],
    };
  }, [t, i18n.language]);

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default AboutJsonLd;
